import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MagicStar } from "iconsax-react";
import { Button, Prompt } from "@/shared/ui";
import { extractErrorMessage } from "@/shared/utils";
import { useResetDemoDataMutation } from "../api/demo";
import { DEMO_SEED_INTENT_KEY } from "../consts";

interface PromptState {
  title: string;
  text: string;
  variant?: "success" | "error" | "warning";
}

export default function DemoDataControls() {
  const { t } = useTranslation("DemoPage");
  const [resetDemoData, { isLoading }] = useResetDemoDataMutation();
  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const autoSeedTriggered = useRef(false);

  async function handleSeed() {
    if (isLoading) return;
    try {
      await resetDemoData().unwrap();
      setPrompt({
        title: t("prompts.seedSuccessTitle"),
        text: t("prompts.seedSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "prompts.seedError", t),
        variant: "error",
      });
    }
  }

  const handleSeedRef = useRef(handleSeed);
  handleSeedRef.current = handleSeed;

  useEffect(function seedAfterCreate() {
    if (autoSeedTriggered.current) return;
    if (sessionStorage.getItem(DEMO_SEED_INTENT_KEY) !== "1") return;
    autoSeedTriggered.current = true;
    sessionStorage.removeItem(DEMO_SEED_INTENT_KEY);
    handleSeedRef.current();
  }, []);

  return (
    <>
      <Button variant="tertiary" size="md" type="button" disabled={isLoading} onClick={handleSeed}>
        <MagicStar size={16} color="currentColor" />
        {t("controls.seed")}
      </Button>

      {isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("messages.loader.seeding")}
          additionalText={t("messages.loader.pleaseWait")}
          namespace="DemoPage"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={() => setPrompt(null)}
          namespace="DemoPage"
        />
      )}
    </>
  );
}
